// Firebase App (the core Firebase SDK) is always required and must be listed first
import firebase from 'firebase/app'
// Add the Firebase products that you want to use
import 'firebase/auth'
import 'firebase/database'
import cookie from 'js-cookie'

const INVALID_CUSTOM_TOKEN = 'auth/invalid-custom-token'
const FIREBASE_JWT_COOKIE_NAME = 'FIREBASE_JWT'
const VNW_WS_COOKIE = 'VNWWS128450527232960'
export const FIREBASE_APP_NAME_VNW_MESSAGE = 'FIREBASE_VNW_MESSAGE'

type TAuthentication = firebase.auth.UserCredential | undefined

class FireBaseService {
    firebase: firebase.app.App
    refreshJWTUrl: string
    refreshJWTRetry: number
    firstAuth: boolean

    readonly TAG = '[FireBaseService]'

    constructor(config: any) {
        try {
            const firebaseApp = firebase.app(FIREBASE_APP_NAME_VNW_MESSAGE)
            this.firebase = firebaseApp
        } catch (e) {
            console.debug(this.TAG, `will create a new instance of ${FIREBASE_APP_NAME_VNW_MESSAGE} `)
            this.firebase = firebase.initializeApp(config, FIREBASE_APP_NAME_VNW_MESSAGE)
        }

        this.refreshJWTUrl = config.refreshJWTUrl
        this.refreshJWTRetry = 0
        this.firstAuth = false
    }

    async _handleAuthFail(error: any) {
        const errorCode = error.code
        console.error('Retry when firebase authentication failed', error)

        await this.getAuth().signOut()

        if (errorCode === INVALID_CUSTOM_TOKEN) {
            // Thrown if the custom token format is incorrect.
            const token = await this.refreshJWT()

            if (token) {
                this.refreshJWTRetry++
                return this.authenticate(token)
            }
        }

        return undefined
    }

    async authenticate(token: string): Promise<TAuthentication> {
        try {
            return await this.getAuth().signInWithCustomToken(token)
        } catch (err) {
            if (this.refreshJWTRetry < 3) {
                return await this._handleAuthFail(err)
            }
            console.error('Firebase Authentication failed ' + this.refreshJWTRetry, err)
            return undefined
        }
    }

    async authenticatedConnect() {
        // for the first time authentication, always need to use JWT stored in cookie to verify the user
        // otherwise firebase will recognise the previous logged in user base on session
        if (!this.firstAuth) {
            // check if firebase has already signed in
            if (this.getAuth().currentUser) {
                return Promise.resolve(this.getAuth())
            }
        }

        // look for JWT token in cookie
        let jwtToken = cookie.get(FIREBASE_JWT_COOKIE_NAME)
        if (!jwtToken) {
            this.getAuth().signOut()
            jwtToken = await this.refreshJWT()
        }

        if (!jwtToken) {
            console.log('Cannot authenticate firebase at the moment')
            return Promise.resolve()
        }

        this.firstAuth = true

        return this.authenticate(jwtToken)
    }

    getAuth() {
        return this.firebase.auth()
    }

    getDatabase() {
        return this.firebase.database()
    }

    async getUserId() {
        const currentUser = this.getAuth().currentUser
        if (currentUser) {
            return Promise.resolve(currentUser.uid)
        } else {
            return new Promise((resolve) => {
                this.getAuth().onAuthStateChanged(async (user) => {
                    if (user) {
                        resolve(user.uid)
                    } else {
                        resolve(null)
                    }
                })
            })
        }
    }

    refreshJWT = async () => {
        let response
        try {
            const vnwWSCookie = cookie.get(VNW_WS_COOKIE)
            // Without VNW_WS_COOKIE then there is no need to call refresh JWT
            if (!vnwWSCookie) {
                return
            }
            response = await fetch(this.refreshJWTUrl)

            if (response.status === 200) {
                const responseData = await response.json()
                if (responseData.jwt) {
                    if (typeof window !== 'undefined') {
                        cookie.set('FIREBASE_JWT', responseData.jwt, { expires: 90 })
                    }

                    return Promise.resolve(responseData.jwt)
                }
            }
        } catch (error) {
            console.error('[Error Caught] refresh JWT', response)
        }

        return null
    }

    signOut() {
        return this.getAuth().signOut()
    }
}

export default FireBaseService
